import { z } from 'zod';
import '@noble/hashes/sha256';

// src/tenancy/rungs.ts
var RUNG_ORDER = ["global", "customer", "org", "tenant", "user"];
var RUNG_ALIASES = {
  system: "global",
  platform: "global"
};
function isRung(value) {
  return typeof value === "string" && RUNG_ORDER.includes(value);
}
function canonicalRung(value) {
  if (typeof value !== "string") return null;
  const lower = value.trim().toLowerCase();
  if (isRung(lower)) return lower;
  return RUNG_ALIASES[lower] ?? null;
}
function assertRungSet(label, ids) {
  const got = [...new Set(ids)].sort();
  const want = [...RUNG_ORDER].sort();
  if (got.length !== want.length || got.some((id, i) => id !== want[i])) {
    throw new Error(
      `${label} is not the tenancy ladder.
  expected (commons.tenancy.ladder.RUNG_ORDER): ${want.join(", ")}
  got: ${got.join(", ")}
If a rung was added or renamed, change it in commons/tenancy/ladder.py first and mirror it in fe-commons/tenancy/rungs.ts \u2014 never the other way round.`
    );
  }
}
var TENANCY_RUNGS_KEY = "tenancy_rungs";
function offeredRungs(planMetadata, subscriptionMetadata) {
  return rungsFrom(subscriptionMetadata) ?? rungsFrom(planMetadata);
}
function rungsFrom(metadata) {
  const raw = metadata?.[TENANCY_RUNGS_KEY];
  if (!Array.isArray(raw)) return null;
  const known = raw.map((value) => canonicalRung(value)).filter((r) => r !== null);
  return known.length > 0 ? new Set(known) : null;
}
function ladderIsChoosable(offered) {
  return !offered || offered.size > 1;
}
var WIDER_THAN_TENANT = ["org", "customer", "global"];
function ladderIsShared(offered) {
  if (!offered) return true;
  return WIDER_THAN_TENANT.some((rung) => offered.has(rung));
}
function rungOffered(rung, offered) {
  if (!offered) return true;
  const canonical = canonicalRung(rung);
  return canonical === null || offered.has(canonical);
}
function offeredOptions(options, offered, pick) {
  if (!offered) return options;
  const kept = options.filter((option) => rungOffered(pick(option), offered));
  return kept.length > 0 ? kept : options;
}
function rungOrOffered(stored, offered, fallback) {
  return rungOffered(stored, offered) ? stored : fallback;
}

// src/tenancy/defaults.ts
function stableFirst(items) {
  return items.reduce((best, it) => {
    const byTime = (it.created_at ?? 0) - (best.created_at ?? 0);
    if (byTime < 0) return it;
    if (byTime > 0) return best;
    return (it.id ?? "").localeCompare(best.id ?? "") < 0 ? it : best;
  });
}
function selectDefault(items, preferredId) {
  if (items.length === 0) return void 0;
  if (preferredId) {
    const preferred = items.find((it) => it.id === preferredId);
    if (preferred) return preferred;
  }
  const roots = items.filter((it) => it.is_root);
  if (roots.length > 0) return stableFirst(roots);
  const sysDefaults = items.filter((it) => it.system_default);
  if (sysDefaults.length > 0) return stableFirst(sysDefaults);
  return stableFirst(items);
}
function selectDefaultOrg(orgs, preferredId) {
  return selectDefault(orgs, preferredId);
}
function selectDefaultTenant(tenants, preferredId) {
  return selectDefault(tenants, preferredId);
}
function findRootOrg(orgs) {
  return orgs.find((o) => o.is_root);
}
function findRootTenant(tenants) {
  return tenants.find((t) => t.is_root);
}

// src/tenancy/constants.ts
var GLOBAL_TENANT = "main";
var GLOBAL_TENANT_COMPOSITE = "main-main";

// src/singleton/index.ts
var NAMESPACE = "@authe/fe-commons/singleton/";
function globalSingleton(key, init) {
  const slot = /* @__PURE__ */ Symbol.for(NAMESPACE + key);
  const realm = globalThis;
  const existing = realm[slot];
  if (existing !== void 0) return existing;
  const created = init();
  realm[slot] = created;
  return created;
}
var BRAND_NAMESPACE = "@authe/fe-commons/brand/";
var ordinaryHasInstance = Function.prototype[Symbol.hasInstance];
function brandClass(cls, key) {
  const brand = /* @__PURE__ */ Symbol.for(BRAND_NAMESPACE + key);
  Object.defineProperty(cls.prototype, brand, { value: true });
  Object.defineProperty(cls, Symbol.hasInstance, {
    value(value) {
      if (this !== cls) return ordinaryHasInstance.call(this, value);
      return typeof value === "object" && value !== null && value[brand] === true;
    }
  });
}

// src/logging/index.ts
var LEVEL_WEIGHT = { debug: 10, info: 20, warn: 30, error: 40 };
var DEBUG_FLAG_KEY = "authe:debug";
function storage() {
  try {
    const s = typeof localStorage !== "undefined" ? localStorage : void 0;
    return s && typeof s.getItem === "function" ? s : void 0;
  } catch {
    return void 0;
  }
}
function isDebugEnabled() {
  try {
    if (typeof location !== "undefined") {
      const param = new URLSearchParams(location.search).get("debug");
      if (param === "1") {
        enableDebug();
        return true;
      }
      if (param === "0") {
        disableDebug();
        return false;
      }
    }
  } catch {
  }
  return storage()?.getItem(DEBUG_FLAG_KEY) === "1";
}
function enableDebug() {
  storage()?.setItem(DEBUG_FLAG_KEY, "1");
}
function disableDebug() {
  storage()?.removeItem(DEBUG_FLAG_KEY);
}
var logging = globalSingleton("logging/state", () => ({
  sinks: [],
  /** Minimum level that reaches the console (debug-flag lowers it to `debug`). */
  minConsoleLevel: "warn"
}));
function createLogger(namespace) {
  function emit(level, message, context) {
    const record = { level, namespace, message, context, timestamp: Date.now() };
    for (const sink of logging.sinks) {
      try {
        sink(record);
      } catch {
      }
    }
    const threshold = isDebugEnabled() ? LEVEL_WEIGHT.debug : LEVEL_WEIGHT[logging.minConsoleLevel];
    if (LEVEL_WEIGHT[level] < threshold) return;
    const prefix = `[${namespace}]`;
    const fn = level === "debug" ? console.debug : level === "info" ? console.info : level === "warn" ? console.warn : console.error;
    if (context) fn(prefix, message, context);
    else fn(prefix, message);
  }
  return {
    debug: (m, c) => emit("debug", m, c),
    info: (m, c) => emit("info", m, c),
    warn: (m, c) => emit("warn", m, c),
    error: (m, c) => emit("error", m, c),
    child: (suffix) => createLogger(`${namespace}:${suffix}`)
  };
}

// src/chains/family.ts
var ChainFamily = {
  EVM: "evm",
  UTXO: "utxo",
  SOLANA: "solana",
  COSMOS: "cosmos",
  /** Hyperliquid's L1 (HyperCore): one signed action per transaction, EVM-shaped identifiers. */
  HYPERCORE: "hypercore",
  XRPL: "xrpl",
  TRON: "tron",
  NEAR: "near",
  OTHER: "other"
};
function isChainFamily(value) {
  return typeof value === "string" && Object.values(ChainFamily).includes(value);
}

// src/chains/catalogue.generated.json
var catalogue_generated_default = {
  chains: [
    {
      aliases: [
        "ape-chain",
        "apechain-mainnet"
      ],
      bech32_hrp: null,
      block_time_seconds: 0.9,
      brand_color: null,
      caip2: "eip155:33139",
      chain_id_hex: "0x8173",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://apescan.io",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l2",
      mainnet: null,
      metadata: {},
      name: "ApeChain",
      native_token: "APE",
      providers: {
        blockscout: "https://apechain.calderaexplorer.xyz/api",
        blockscout_v2: "https://apechain.calderaexplorer.xyz/api/v2",
        coingecko: "apechain",
        coingecko_native: "apecoin",
        defillama: "apechain",
        dexscreener: "apechain",
        evm_rpc: "discovery",
        sourcify: "33139"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.apechain.com/http"
      ],
      slug: "apechain",
      status: "active",
      wrapped_native_address: "0x48b62137edfa95a428d35c09e44256a739f6b557"
    },
    {
      aliases: [
        "curtis",
        "apechain-curtis",
        "apechain-testnet"
      ],
      bech32_hrp: null,
      block_time_seconds: 0.9,
      brand_color: null,
      caip2: "eip155:33111",
      chain_id_hex: "0x8157",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.curtis.apechain.com",
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l2",
      mainnet: "eip155:33139",
      metadata: {},
      name: "ApeChain Curtis",
      native_token: "APE",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.curtis.apechain.com"
      ],
      slug: "apechain_curtis",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "arbitrum-one",
        "arb",
        "arbitrum-mainnet"
      ],
      bech32_hrp: null,
      block_time_seconds: 0.25,
      brand_color: "#28A0F0",
      caip2: "eip155:42161",
      chain_id_hex: "0xa4b1",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://arbiscan.io",
      family: "evm",
      finality_blocks: 32,
      is_testnet: false,
      layer: "l2",
      mainnet: null,
      metadata: {},
      name: "Arbitrum",
      native_token: "ETH",
      providers: {
        alchemy: "arb-mainnet",
        ankr: "arbitrum",
        arkham: "arbitrum_one",
        blockchair: "arbitrum",
        blockscan: "42161",
        blockscout: "https://arbitrum.blockscout.com/api",
        blocksec: "42161",
        chainflip: "4",
        codeslaw: "arbitrum",
        coingecko: "arbitrum-one",
        coingecko_native: "ethereum",
        defillama: "arbitrum",
        dexscreener: "arbitrum",
        etherscan_scan: "arbiscan.io",
        infura: "arbitrum-mainnet",
        metasleuth: "arbitrum",
        moralis: "arbitrum",
        moralis_wallet: "arbitrum",
        sourcify: "42161",
        thorchain: "ARB"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://arb1.arbitrum.io/rpc",
        "https://arbitrum-one-rpc.publicnode.com"
      ],
      slug: "arbitrum",
      status: "active",
      wrapped_native_address: "0x82af49447d8a07e3bd95bd0d56f35241523fbab1"
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 0.25,
      brand_color: "#28A0F0",
      caip2: "eip155:421614",
      chain_id_hex: "0x66eee",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://sepolia.arbiscan.io",
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l2",
      mainnet: "eip155:42161",
      metadata: {},
      name: "Arbitrum Sepolia",
      native_token: "ETH",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "arbitrum_sepolia",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 1,
      brand_color: "#70D44B",
      caip2: "eip155:1313161554",
      chain_id_hex: "0x4e454152",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.aurora.dev",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "sidechain",
      mainnet: null,
      metadata: {},
      name: "Aurora",
      native_token: "ETH",
      providers: {
        coingecko_native: "ethereum",
        defillama: "aurora",
        dexscreener: "aurora"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "aurora",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 1,
      brand_color: "#70D44B",
      caip2: "eip155:1313161555",
      chain_id_hex: "0x4e454153",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.testnet.aurora.dev",
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "sidechain",
      mainnet: "eip155:1313161554",
      metadata: {},
      name: "Aurora Testnet",
      native_token: "ETH",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "aurora_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "avax",
        "avalanche-c-chain",
        "avax-c-chain",
        "avalanche-mainnet"
      ],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#E84142",
      caip2: "eip155:43114",
      chain_id_hex: "0xa86a",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://snowtrace.io",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l1",
      mainnet: null,
      metadata: {},
      name: "Avalanche",
      native_token: "AVAX",
      providers: {
        alchemy: "avax-mainnet",
        ankr: "avalanche",
        arkham: "avalanche",
        blockchair: "avalanche",
        blockscan: "43114",
        blocksec: "43114",
        coingecko: "avalanche",
        coingecko_native: "avalanche-2",
        defillama: "avax",
        dexscreener: "avalanche",
        etherscan_scan: "snowtrace.io",
        infura: "avalanche-mainnet",
        metasleuth: "avalanche",
        moralis: "avalanche",
        moralis_wallet: "avalanche",
        sourcify: "43114",
        thorchain: "AVAX"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://api.avax.network/ext/bc/C/rpc",
        "https://avalanche-c-chain-rpc.publicnode.com"
      ],
      slug: "avalanche",
      status: "active",
      wrapped_native_address: "0xb31f66aa3c1e785363f0875a1b74e27b85fd66c7"
    },
    {
      aliases: [
        "base-mainnet"
      ],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#0052FF",
      caip2: "eip155:8453",
      chain_id_hex: "0x2105",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://basescan.org",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l2",
      mainnet: null,
      metadata: {},
      name: "Base",
      native_token: "ETH",
      providers: {
        alchemy: "base-mainnet",
        ankr: "base",
        arkham: "base",
        blockchair: "base",
        blockscan: "8453",
        blockscout: "https://base.blockscout.com/api",
        codeslaw: "base",
        coingecko: "base",
        coingecko_native: "ethereum",
        defillama: "base",
        dexscreener: "base",
        etherscan_scan: "basescan.org",
        infura: "base-mainnet",
        metasleuth: "base",
        moralis: "base",
        moralis_wallet: "base",
        sourcify: "8453",
        thorchain: "BASE"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://mainnet.base.org",
        "https://base-rpc.publicnode.com"
      ],
      slug: "base",
      status: "active",
      wrapped_native_address: "0x4200000000000000000000000000000000000006"
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#0052FF",
      caip2: "eip155:84531",
      chain_id_hex: "0x14a33",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l2",
      mainnet: "eip155:8453",
      metadata: {},
      name: "Base Goerli",
      native_token: "ETH",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "base_goerli",
      status: "deprecated",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#0052FF",
      caip2: "eip155:84532",
      chain_id_hex: "0x14a34",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://sepolia.basescan.org",
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l2",
      mainnet: "eip155:8453",
      metadata: {},
      name: "Base Sepolia",
      native_token: "ETH",
      providers: {
        moralis: "base_sepolia"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "base_sepolia",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "bitcoin-mainnet",
        "0x0",
        "btc"
      ],
      bech32_hrp: null,
      block_time_seconds: 600,
      brand_color: "#F7931A",
      caip2: "bip122:000000000019d6689c085ae165831e93",
      chain_id_hex: null,
      decimals: 8,
      explorer_paths: {},
      explorer_url: "https://blockstream.info",
      family: "utxo",
      finality_blocks: null,
      is_testnet: false,
      layer: null,
      mainnet: null,
      metadata: {},
      name: "Bitcoin",
      native_token: "BTC",
      providers: {
        blockchair: "bitcoin",
        chainflip: "3",
        coingecko_native: "bitcoin",
        dexscreener: "bitcoin",
        esplora: "mainnet",
        scanscope: "bitcoin",
        thorchain: "BTC"
      },
      reorg_depth: 6,
      rpc_urls: [],
      slug: "bitcoin",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 600,
      brand_color: "#F7931A",
      caip2: "bip122:000000000933ea01ad0ee984209779ba",
      chain_id_hex: null,
      decimals: 8,
      explorer_paths: {},
      explorer_url: null,
      family: "utxo",
      finality_blocks: null,
      is_testnet: true,
      layer: null,
      mainnet: "bip122:000000000019d6689c085ae165831e93",
      metadata: {},
      name: "Bitcoin Testnet",
      native_token: "BTC",
      providers: {
        esplora: "testnet"
      },
      reorg_depth: 6,
      rpc_urls: [],
      slug: "bitcoin-testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "blast-mainnet"
      ],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#FCFC03",
      caip2: "eip155:81457",
      chain_id_hex: "0x13e31",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://blastscan.io",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l2",
      mainnet: null,
      metadata: {},
      name: "Blast",
      native_token: "ETH",
      providers: {
        arkham: "blast",
        codeslaw: "blast",
        coingecko_native: "ethereum",
        defillama: "blast",
        dexscreener: "blast",
        sourcify: "81457"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://blast-rpc.publicnode.com"
      ],
      slug: "blast",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#FCFC03",
      caip2: "eip155:168587773",
      chain_id_hex: "0xa0c71fd",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://sepolia.blastscan.io",
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l2",
      mainnet: "eip155:81457",
      metadata: {},
      name: "Blast Sepolia",
      native_token: "ETH",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "blast_sepolia",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "binance",
        "binance-smart-chain",
        "bnb",
        "bnb-chain",
        "bnb-smart-chain"
      ],
      bech32_hrp: null,
      block_time_seconds: 3,
      brand_color: "#F0B90B",
      caip2: "eip155:56",
      chain_id_hex: "0x38",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://bscscan.com",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l1",
      mainnet: null,
      metadata: {},
      name: "BNB Chain",
      native_token: "BNB",
      providers: {
        alchemy: "bnb-mainnet",
        ankr: "bsc",
        arkham: "bsc",
        blockchair: "binance-smart-chain",
        blockscan: "56",
        blocksec: "56",
        chainflip: "8",
        codeslaw: "bnbchain",
        coingecko: "binance-smart-chain",
        coingecko_native: "binancecoin",
        defillama: "bsc",
        dexscreener: "bsc",
        etherscan_scan: "bscscan.com",
        infura: "bsc-mainnet",
        metasleuth: "bsc",
        moralis: "bsc",
        moralis_wallet: "bsc",
        sourcify: "56",
        thorchain: "BSC"
      },
      reorg_depth: 15,
      rpc_urls: [
        "https://bsc-dataseed1.binance.org",
        "https://bsc-rpc.publicnode.com"
      ],
      slug: "bsc",
      status: "active",
      wrapped_native_address: "0xbb4cdb9cbd36b01bd1cbaebf2de08d9173bc095c"
    },
    {
      aliases: [
        "bnb-testnet"
      ],
      bech32_hrp: null,
      block_time_seconds: 3,
      brand_color: "#F0B90B",
      caip2: "eip155:97",
      chain_id_hex: "0x61",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://testnet.bscscan.com",
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l1",
      mainnet: "eip155:56",
      metadata: {},
      name: "BSC Testnet",
      native_token: "tBNB",
      providers: {
        blockchair: "bsc-testnet",
        infura: "bsc-testnet",
        moralis: "bsc_testnet"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "bsc_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 5,
      brand_color: "#FCFF52",
      caip2: "eip155:42220",
      chain_id_hex: "0xa4ec",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://celoscan.io",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l1",
      mainnet: null,
      metadata: {},
      name: "Celo",
      native_token: "CELO",
      providers: {
        coingecko_native: "celo",
        defillama: "celo",
        dexscreener: "celo"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://celo-rpc.publicnode.com"
      ],
      slug: "celo",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 5,
      brand_color: "#FCFF52",
      caip2: "eip155:44787",
      chain_id_hex: "0xaef3",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l1",
      mainnet: "eip155:42220",
      metadata: {},
      name: "Celo Alfajores",
      native_token: "CELO",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "celo_alfajores",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:88888",
      chain_id_hex: "0x15b38",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: null,
      mainnet: null,
      metadata: {},
      name: "Chiliz",
      native_token: "CHZ",
      providers: {
        coingecko_native: "chiliz",
        moralis: "chiliz",
        moralis_wallet: "chiliz"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "chiliz",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:88882",
      chain_id_hex: "0x15b32",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: null,
      mainnet: "eip155:88888",
      metadata: {},
      name: "Chiliz Testnet",
      native_token: "CHZ",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "chiliz_spicy",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "cosmoshub-mainnet",
        "cosmoshub-4",
        "gaia"
      ],
      bech32_hrp: "cosmos",
      block_time_seconds: 5.5,
      brand_color: "#2E3148",
      caip2: "cosmos:cosmoshub-4",
      chain_id_hex: null,
      decimals: 6,
      explorer_paths: {
        token: null
      },
      explorer_url: "https://www.mintscan.io/cosmos",
      family: "cosmos",
      finality_blocks: null,
      is_testnet: false,
      layer: null,
      mainnet: null,
      metadata: {},
      name: "Cosmos Hub",
      native_token: "ATOM",
      providers: {
        coingecko_native: "cosmos",
        cosmos_directory: "https://rest.cosmos.directory/cosmoshub",
        cosmos_lcd: "https://cosmos-rest.publicnode.com",
        thorchain: "GAIA"
      },
      reorg_depth: 1,
      rpc_urls: [],
      slug: "cosmoshub",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "cro"
      ],
      bech32_hrp: null,
      block_time_seconds: 6,
      brand_color: "#002D74",
      caip2: "eip155:25",
      chain_id_hex: "0x19",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://cronoscan.com",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "sidechain",
      mainnet: null,
      metadata: {},
      name: "Cronos",
      native_token: "CRO",
      providers: {
        arkham: "cronos",
        coingecko_native: "crypto-com-chain",
        defillama: "cronos",
        dexscreener: "cronos",
        moralis: "cronos",
        moralis_wallet: "cronos"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "cronos",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 13,
      brand_color: "#328332",
      caip2: "eip155:61",
      chain_id_hex: "0x3d",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://blockscout.com/etc/mainnet",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l1",
      mainnet: null,
      metadata: {
        note: "ETC shares pre-fork tx hashes with ETH. Audit sec. 2 calls out this collision risk."
      },
      name: "Ethereum Classic",
      native_token: "ETC",
      providers: {
        blockscout: "https://etc.blockscout.com/api",
        coingecko_native: "ethereum-classic"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://etc.etcdesktop.com"
      ],
      slug: "etc",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "ethereum",
        "ethereum-mainnet",
        "eth-mainnet"
      ],
      bech32_hrp: null,
      block_time_seconds: 12,
      brand_color: "#627EEA",
      caip2: "eip155:1",
      chain_id_hex: "0x1",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://etherscan.io",
      family: "evm",
      finality_blocks: 96,
      is_testnet: false,
      layer: "l1",
      mainnet: null,
      metadata: {},
      name: "Ethereum",
      native_token: "ETH",
      providers: {
        alchemy: "eth-mainnet",
        ankr: "eth",
        arkham: "ethereum",
        blockchair: "ethereum",
        blockscan: "1",
        blockscout: "https://eth.blockscout.com/api",
        blocksec: "1",
        chainflip: "1",
        codeslaw: "ethereum",
        coingecko: "ethereum",
        coingecko_native: "ethereum",
        defillama: "ethereum",
        dexscreener: "ethereum",
        etherscan_scan: "etherscan.io",
        infura: "mainnet",
        metasleuth: "eth",
        moralis: "eth",
        moralis_wallet: "eth",
        scanscope: "eth",
        sourcify: "1",
        thorchain: "ETH"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://ethereum-rpc.publicnode.com",
        "https://eth.drpc.org",
        "https://mainnet.gateway.tenderly.co"
      ],
      slug: "eth",
      status: "active",
      wrapped_native_address: "0xc02aaa39b223fe8d0a0e5c4f27ead9083c756cc2"
    },
    {
      aliases: [
        "ftm",
        "fantom-opera"
      ],
      bech32_hrp: null,
      block_time_seconds: 1,
      brand_color: "#1969FF",
      caip2: "eip155:250",
      chain_id_hex: "0xfa",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://ftmscan.com",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l1",
      mainnet: null,
      metadata: {},
      name: "Fantom",
      native_token: "FTM",
      providers: {
        blockchair: "fantom",
        blockscan: "250",
        blocksec: "250",
        coingecko: "fantom",
        coingecko_native: "fantom",
        defillama: "fantom",
        dexscreener: "fantom",
        etherscan_scan: "ftmscan.com",
        metasleuth: "fantom",
        sourcify: "250"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpcapi.fantom.network"
      ],
      slug: "fantom",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 1,
      brand_color: "#1969FF",
      caip2: "eip155:4002",
      chain_id_hex: "0xfa2",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l1",
      mainnet: "eip155:250",
      metadata: {},
      name: "Fantom Testnet",
      native_token: "FTM",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "fantom_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:747",
      chain_id_hex: "0x2eb",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://evm.flowscan.io",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l1",
      mainnet: null,
      metadata: {},
      name: "Flow",
      native_token: "FLOW",
      providers: {
        coingecko_native: "flow",
        moralis: "flow",
        moralis_wallet: "flow"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "flow",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:545",
      chain_id_hex: "0x221",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://testnet.evm.flow.com",
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l1",
      mainnet: "eip155:747",
      metadata: {},
      name: "Flow Testnet",
      native_token: "FLOW",
      providers: {
        moralis: "flow_testnet"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "flow_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 5,
      brand_color: "#B4F9BA",
      caip2: "eip155:122",
      chain_id_hex: "0x7a",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l1",
      mainnet: null,
      metadata: {},
      name: "Fuse",
      native_token: "FUSE",
      providers: {
        coingecko_native: "fuse-network-token"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "fuse",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 5,
      brand_color: "#B4F9BA",
      caip2: "eip155:123",
      chain_id_hex: "0x7b",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.fusespark.io",
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l1",
      mainnet: "eip155:122",
      metadata: {},
      name: "Fuse Sparknet",
      native_token: "SPARK",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "fuse_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "xdai",
        "gnosis-chain"
      ],
      bech32_hrp: null,
      block_time_seconds: 5,
      brand_color: "#0B6E4F",
      caip2: "eip155:100",
      chain_id_hex: "0x64",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://gnosisscan.io",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "sidechain",
      mainnet: null,
      metadata: {},
      name: "Gnosis",
      native_token: "xDAI",
      providers: {
        ankr: "gnosis",
        arkham: "gnosis",
        blockchair: "gnosis",
        blockscan: "100",
        coingecko_native: "xdai",
        defillama: "xdai",
        dexscreener: "gnosis",
        moralis: "gnosis",
        moralis_wallet: "gnosis"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.gnosischain.com"
      ],
      slug: "gnosis",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 5,
      brand_color: "#0B6E4F",
      caip2: "eip155:10200",
      chain_id_hex: "0x27d8",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://gnosis-chiado.blockscout.com",
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "sidechain",
      mainnet: "eip155:100",
      metadata: {},
      name: "Gnosis Chiado",
      native_token: "xDAI",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "gnosis_chiado",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 12,
      brand_color: "#627EEA",
      caip2: "eip155:5",
      chain_id_hex: "0x5",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://goerli.etherscan.io",
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l1",
      mainnet: "eip155:1",
      metadata: {},
      name: "Goerli",
      native_token: "ETH",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "goerli",
      status: "deprecated",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:325",
      chain_id_hex: "0x145",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l2",
      mainnet: null,
      metadata: {},
      name: "GRVT",
      native_token: "GBT",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "grvt",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#00AEE9",
      caip2: "eip155:1666600000",
      chain_id_hex: "0x63564c40",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l1",
      mainnet: null,
      metadata: {},
      name: "Harmony",
      native_token: "ONE",
      providers: {
        coingecko_native: "harmony",
        defillama: "harmony",
        dexscreener: "harmony"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "harmony",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#00AEE9",
      caip2: "eip155:1666700000",
      chain_id_hex: "0x6357d2e0",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l1",
      mainnet: "eip155:1666600000",
      metadata: {},
      name: "Harmony Testnet",
      native_token: "ONE",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "harmony_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 12,
      brand_color: "#627EEA",
      caip2: "eip155:17000",
      chain_id_hex: "0x4268",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l1",
      mainnet: "eip155:1",
      metadata: {},
      name: "Holesky",
      native_token: "ETH",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "holesky",
      status: "deprecated",
      wrapped_native_address: null
    },
    {
      aliases: [
        "hypercore-mainnet",
        "hyperliquid",
        "hyperliquid-core",
        "hyperliquid-l1"
      ],
      bech32_hrp: null,
      block_time_seconds: 0.2,
      brand_color: "#97FCE4",
      caip2: "hyperliq:mainnet",
      chain_id_hex: null,
      decimals: 8,
      explorer_paths: {
        token: null
      },
      explorer_url: "https://app.hyperliquid.xyz/explorer",
      family: "hypercore",
      finality_blocks: null,
      is_testnet: false,
      layer: null,
      mainnet: null,
      metadata: {},
      name: "HyperCore",
      native_token: "HYPE",
      providers: {
        coingecko_native: "hyperliquid",
        hyperliquid: "mainnet"
      },
      reorg_depth: 1,
      rpc_urls: [],
      slug: "hypercore",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "hyperliquid-testnet",
        "hypercore_testnet"
      ],
      bech32_hrp: null,
      block_time_seconds: 0.2,
      brand_color: "#97FCE4",
      caip2: "hyperliq:testnet",
      chain_id_hex: null,
      decimals: 8,
      explorer_paths: {
        token: null
      },
      explorer_url: "https://app.hyperliquid-testnet.xyz/explorer",
      family: "hypercore",
      finality_blocks: null,
      is_testnet: true,
      layer: null,
      mainnet: "hyperliq:mainnet",
      metadata: {},
      name: "HyperCore Testnet",
      native_token: "HYPE",
      providers: {
        hyperliquid: "testnet"
      },
      reorg_depth: 1,
      rpc_urls: [],
      slug: "hypercore-testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "hyper-evm",
        "hyperliquid-evm"
      ],
      bech32_hrp: null,
      block_time_seconds: 1,
      brand_color: "#97FCE4",
      caip2: "eip155:999",
      chain_id_hex: "0x3e7",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://hyperevmscan.io",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l1",
      mainnet: null,
      metadata: {},
      name: "HyperEVM",
      native_token: "HYPE",
      providers: {
        coingecko_native: "hyperliquid",
        evm_rpc: "ingest",
        sourcify: "999"
      },
      reorg_depth: 1,
      rpc_urls: [
        "https://rpc.hyperliquid.xyz/evm"
      ],
      slug: "hyperevm",
      status: "active",
      wrapped_native_address: "0x5555555555555555555555555555555555555555"
    },
    {
      aliases: [
        "hyperevm-testnet",
        "hyperliquid-evm-testnet"
      ],
      bech32_hrp: null,
      block_time_seconds: 1,
      brand_color: "#97FCE4",
      caip2: "eip155:998",
      chain_id_hex: "0x3e6",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l1",
      mainnet: "eip155:999",
      metadata: {},
      name: "HyperEVM Testnet",
      native_token: "HYPE",
      providers: {
        evm_rpc: "ingest",
        sourcify: "998"
      },
      reorg_depth: 1,
      rpc_urls: [
        "https://rpc.hyperliquid-testnet.xyz/evm"
      ],
      slug: "hyperevm_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:57073",
      chain_id_hex: "0xdef1",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.inkonchain.com",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l2",
      mainnet: null,
      metadata: {},
      name: "Ink",
      native_token: "ETH",
      providers: {
        blockscout_v2: "https://explorer.inkonchain.com/api/v2",
        coingecko_native: "ethereum",
        sourcify: "57073"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "ink",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: "#FF433E",
      caip2: "eip155:2222",
      chain_id_hex: "0x8ae",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l1",
      mainnet: null,
      metadata: {},
      name: "Kava",
      native_token: "KAVA",
      providers: {
        coingecko_native: "kava",
        defillama: "kava"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "kava",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: "#FF433E",
      caip2: "eip155:2221",
      chain_id_hex: "0x8ad",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l1",
      mainnet: "eip155:2222",
      metadata: {},
      name: "Kava Testnet",
      native_token: "TKAVA",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "kava_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 1,
      brand_color: "#FF1B1B",
      caip2: "eip155:8217",
      chain_id_hex: "0x2019",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l1",
      mainnet: null,
      metadata: {},
      name: "Klaytn",
      native_token: "KLAY",
      providers: {
        coingecko_native: "kaia",
        defillama: "klaytn"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "klaytn",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 1,
      brand_color: "#FF1B1B",
      caip2: "eip155:1001",
      chain_id_hex: "0x3e9",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l1",
      mainnet: "eip155:8217",
      metadata: {},
      name: "Klaytn Baobab",
      native_token: "KLAY",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "klaytn_baobab",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "linea-mainnet"
      ],
      bech32_hrp: null,
      block_time_seconds: 3,
      brand_color: "#61DFFF",
      caip2: "eip155:59144",
      chain_id_hex: "0xe708",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://lineascan.build",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l2",
      mainnet: null,
      metadata: {},
      name: "Linea",
      native_token: "ETH",
      providers: {
        arkham: "linea",
        coingecko_native: "ethereum",
        defillama: "linea",
        dexscreener: "linea",
        etherscan_scan: "lineascan.build",
        moralis: "linea",
        moralis_wallet: "linea",
        sourcify: "59144"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.linea.build"
      ],
      slug: "linea",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 3,
      brand_color: "#61DFFF",
      caip2: "eip155:59141",
      chain_id_hex: "0xe705",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://sepolia.lineascan.build",
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l2",
      mainnet: "eip155:59144",
      metadata: {},
      name: "Linea Sepolia",
      native_token: "ETH",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "linea_sepolia",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 3,
      brand_color: "#61DFFF",
      caip2: "eip155:59140",
      chain_id_hex: "0xe704",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l2",
      mainnet: "eip155:59144",
      metadata: {},
      name: "Linea Goerli",
      native_token: "ETH",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "linea_testnet",
      status: "deprecated",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:1135",
      chain_id_hex: "0x46f",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://blockscout.lisk.com",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l2",
      mainnet: null,
      metadata: {},
      name: "Lisk",
      native_token: "LSK",
      providers: {
        coingecko_native: "lisk",
        moralis: "lisk",
        moralis_wallet: "lisk"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "lisk",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:4202",
      chain_id_hex: "0x106a",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l2",
      mainnet: "eip155:1135",
      metadata: {},
      name: "Lisk Sepolia",
      native_token: "LSK",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "lisk_sepolia",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#000000",
      caip2: "eip155:5000",
      chain_id_hex: "0x1388",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.mantle.xyz",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l2",
      mainnet: null,
      metadata: {},
      name: "Mantle",
      native_token: "MNT",
      providers: {
        arkham: "mantle",
        coingecko_native: "mantle",
        defillama: "mantle",
        dexscreener: "mantle"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.mantle.xyz"
      ],
      slug: "mantle",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "mantle-testnet",
        "mantle_testnet"
      ],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#000000",
      caip2: "eip155:5003",
      chain_id_hex: "0x138b",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://sepolia.mantlescan.xyz",
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l2",
      mainnet: "eip155:5000",
      metadata: {},
      name: "Mantle Sepolia",
      native_token: "MNT",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "mantle_sepolia",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 4,
      brand_color: "#00DACC",
      caip2: "eip155:1088",
      chain_id_hex: "0x440",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l2",
      mainnet: null,
      metadata: {},
      name: "Metis",
      native_token: "METIS",
      providers: {
        coingecko_native: "metis-token",
        defillama: "metis"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "metis",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 4,
      brand_color: "#00DACC",
      caip2: "eip155:588",
      chain_id_hex: "0x24c",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l2",
      mainnet: "eip155:1088",
      metadata: {},
      name: "Metis Goerli",
      native_token: "METIS",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "metis_goerli",
      status: "deprecated",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:143",
      chain_id_hex: "0x8f",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://monadexplorer.com",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l1",
      mainnet: null,
      metadata: {},
      name: "Monad",
      native_token: "MON",
      providers: {
        coingecko_native: "monad",
        moralis: "monad",
        moralis_wallet: "monad"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "monad",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "monero-mainnet",
        "xmr"
      ],
      bech32_hrp: null,
      block_time_seconds: 120,
      brand_color: "#FF6600",
      caip2: "monero:418015bb9ae982a1975da7d79277c270",
      chain_id_hex: null,
      decimals: 12,
      explorer_paths: {
        address: null,
        token: null
      },
      explorer_url: "https://xmrchain.net",
      family: "utxo",
      finality_blocks: null,
      is_testnet: false,
      layer: null,
      mainnet: null,
      metadata: {},
      name: "Monero",
      native_token: "XMR",
      providers: {
        blockchair: "monero",
        blockchair_api: "monero",
        coingecko_native: "monero",
        xmrchain: "mainnet"
      },
      reorg_depth: 10,
      rpc_urls: [],
      slug: "monero",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 12,
      brand_color: "#53CBC8",
      caip2: "eip155:1287",
      chain_id_hex: "0x507",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://moonbase.moonscan.io",
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l1",
      mainnet: "eip155:1284",
      metadata: {},
      name: "Moonbase",
      native_token: "DEV",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "moonbase",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 12,
      brand_color: "#53CBC8",
      caip2: "eip155:1284",
      chain_id_hex: "0x504",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://moonscan.io",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l1",
      mainnet: null,
      metadata: {},
      name: "Moonbeam",
      native_token: "GLMR",
      providers: {
        coingecko_native: "moonbeam",
        defillama: "moonbeam",
        dexscreener: "moonbeam",
        metasleuth: "moonbeam",
        moralis: "moonbeam",
        moralis_wallet: "moonbeam"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "moonbeam",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 12,
      brand_color: "#C0C0C0",
      caip2: "eip155:1285",
      chain_id_hex: "0x505",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l1",
      mainnet: null,
      metadata: {},
      name: "Moonriver",
      native_token: "MOVR",
      providers: {
        coingecko_native: "moonriver",
        defillama: "moonriver",
        dexscreener: "moonriver",
        moralis: "moonriver"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "moonriver",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#8247E5",
      caip2: "eip155:80001",
      chain_id_hex: "0x13881",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l2",
      mainnet: "eip155:137",
      metadata: {},
      name: "Polygon Mumbai",
      native_token: "MATIC",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "mumbai",
      status: "deprecated",
      wrapped_native_address: null
    },
    {
      aliases: [
        "near-mainnet"
      ],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "near:mainnet",
      chain_id_hex: null,
      decimals: 24,
      explorer_paths: {
        address: "address",
        block: "blocks",
        token: null,
        tx: "txns"
      },
      explorer_url: "https://nearblocks.io",
      family: "near",
      finality_blocks: null,
      is_testnet: false,
      layer: null,
      mainnet: null,
      metadata: {},
      name: "NEAR",
      native_token: "NEAR",
      providers: {
        coingecko_native: "near",
        near_rpc: "https://archival-rpc.mainnet.near.org"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.mainnet.fastnear.com",
        "https://archival-rpc.mainnet.near.org"
      ],
      slug: "near",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "near:testnet",
      chain_id_hex: null,
      decimals: 24,
      explorer_paths: {
        address: "address",
        block: "blocks",
        token: null,
        tx: "txns"
      },
      explorer_url: "https://testnet.nearblocks.io",
      family: "near",
      finality_blocks: null,
      is_testnet: true,
      layer: null,
      mainnet: "near:mainnet",
      metadata: {},
      name: "NEAR Testnet",
      native_token: "NEAR",
      providers: {
        near_rpc: "https://archival-rpc.testnet.near.org"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.testnet.near.org"
      ],
      slug: "near-testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:41443",
      chain_id_hex: "0xa1e3",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer-evm.nesa.ai",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: null,
      mainnet: null,
      metadata: {},
      name: "Nesa",
      native_token: "NES",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "nesa",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:204",
      chain_id_hex: "0xcc",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://opbnbscan.com",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l2",
      mainnet: null,
      metadata: {},
      name: "opBNB",
      native_token: "BNB",
      providers: {
        coingecko_native: "binancecoin",
        defillama: "op_bnb"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "opbnb",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "op",
        "op-mainnet",
        "optimism-mainnet"
      ],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#FF0420",
      caip2: "eip155:10",
      chain_id_hex: "0xa",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://optimistic.etherscan.io",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l2",
      mainnet: null,
      metadata: {},
      name: "Optimism",
      native_token: "ETH",
      providers: {
        alchemy: "opt-mainnet",
        arkham: "optimism",
        blockchair: "optimism",
        blockscan: "10",
        blockscout: "https://explorer.optimism.io/api",
        blocksec: "10",
        codeslaw: "optimism",
        coingecko: "optimistic-ethereum",
        coingecko_native: "ethereum",
        defillama: "optimism",
        dexscreener: "optimism",
        etherscan_scan: "optimistic.etherscan.io",
        infura: "optimism-mainnet",
        metasleuth: "optimism",
        moralis: "optimism",
        moralis_wallet: "optimism",
        sourcify: "10"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://mainnet.optimism.io",
        "https://optimism-rpc.publicnode.com"
      ],
      slug: "optimism",
      status: "active",
      wrapped_native_address: "0x4200000000000000000000000000000000000006"
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#FF0420",
      caip2: "eip155:420",
      chain_id_hex: "0x1a4",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l2",
      mainnet: "eip155:10",
      metadata: {},
      name: "Optimism Goerli",
      native_token: "ETH",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "optimism_goerli",
      status: "deprecated",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#FF0420",
      caip2: "eip155:11155420",
      chain_id_hex: "0xaa37dc",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://sepolia-optimism.etherscan.io",
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l2",
      mainnet: "eip155:10",
      metadata: {},
      name: "Optimism Sepolia",
      native_token: "ETH",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "optimism_sepolia",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "osmosis-mainnet",
        "osmosis-1"
      ],
      bech32_hrp: "osmo",
      block_time_seconds: 1.6,
      brand_color: "#750BBB",
      caip2: "cosmos:osmosis-1",
      chain_id_hex: null,
      decimals: 6,
      explorer_paths: {
        token: null
      },
      explorer_url: "https://www.mintscan.io/osmosis",
      family: "cosmos",
      finality_blocks: null,
      is_testnet: false,
      layer: null,
      mainnet: null,
      metadata: {},
      name: "Osmosis",
      native_token: "OSMO",
      providers: {
        coingecko_native: "osmosis",
        cosmos_directory: "https://rest.cosmos.directory/osmosis",
        cosmos_lcd: "https://osmosis-rest.publicnode.com"
      },
      reorg_depth: 1,
      rpc_urls: [],
      slug: "osmosis",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:11297108109",
      chain_id_hex: "0x2a15c308d",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "sidechain",
      mainnet: null,
      metadata: {},
      name: "Palm",
      native_token: "PALM",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "palm",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:3338",
      chain_id_hex: "0xd0a",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://peaq.subscan.io",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l1",
      mainnet: null,
      metadata: {},
      name: "peaq",
      native_token: "PEAQ",
      providers: {
        coingecko_native: "peaq-2"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "peaq",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:9745",
      chain_id_hex: "0x2611",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://plasmascan.to",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: null,
      mainnet: null,
      metadata: {},
      name: "Plasma",
      native_token: "XPL",
      providers: {
        coingecko_native: "plasma"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "plasma",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "polkadot-mainnet",
        "0x162",
        "dot"
      ],
      bech32_hrp: null,
      block_time_seconds: 6,
      brand_color: "#E6007A",
      caip2: "polkadot:91b171bb158e2d3848fa23a9f1c25182",
      chain_id_hex: null,
      decimals: 10,
      explorer_paths: {},
      explorer_url: "https://polkadot.subscan.io",
      family: "other",
      finality_blocks: null,
      is_testnet: false,
      layer: null,
      mainnet: null,
      metadata: {},
      name: "Polkadot",
      native_token: "DOT",
      providers: {
        chainflip: "2",
        coingecko_native: "polkadot"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "polkadot",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "matic",
        "matic-network",
        "polygon-mainnet",
        "polygon-pos"
      ],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#8247E5",
      caip2: "eip155:137",
      chain_id_hex: "0x89",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://polygonscan.com",
      family: "evm",
      finality_blocks: 512,
      is_testnet: false,
      layer: "l2",
      mainnet: null,
      metadata: {},
      name: "Polygon",
      native_token: "POL",
      providers: {
        alchemy: "polygon-mainnet",
        arkham: "polygon",
        blockchair: "polygon",
        blockscan: "137",
        blockscout: "https://polygon.blockscout.com/api",
        blocksec: "137",
        codeslaw: "polygon",
        coingecko: "polygon-pos",
        coingecko_native: "polygon-ecosystem-token",
        defillama: "polygon",
        dexscreener: "polygon",
        etherscan_scan: "polygonscan.com",
        infura: "polygon-mainnet",
        metasleuth: "polygon",
        moralis: "polygon",
        moralis_wallet: "polygon",
        sourcify: "137"
      },
      reorg_depth: 256,
      rpc_urls: [
        "https://polygon-bor-rpc.publicnode.com",
        "https://polygon.drpc.org",
        "https://polygon.gateway.tenderly.co"
      ],
      slug: "polygon",
      status: "active",
      wrapped_native_address: "0x0d500b1d8e8ef31e21c99d1db9a6444d3adf1270"
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 2,
      brand_color: "#8247E5",
      caip2: "eip155:80002",
      chain_id_hex: "0x13882",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://amoy.polygonscan.com",
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l2",
      mainnet: "eip155:137",
      metadata: {},
      name: "Polygon Amoy",
      native_token: "MATIC",
      providers: {
        moralis: "polygon_amoy"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "polygon_amoy",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "polygon-zkevm",
        "zkevm"
      ],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:1101",
      chain_id_hex: "0x44d",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://zkevm.polygonscan.com",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l2",
      mainnet: null,
      metadata: {},
      name: "Polygon zkEVM",
      native_token: "ETH",
      providers: {
        arkham: "polygon_zkevm",
        coingecko_native: "ethereum"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "polygon_zkevm",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:2442",
      chain_id_hex: "0x98a",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l2",
      mainnet: "eip155:1101",
      metadata: {},
      name: "Polygon zkEVM Cardona",
      native_token: "MATIC",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "polygon_zkevm_cardona",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "pls"
      ],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:369",
      chain_id_hex: "0x171",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://scan.pulsechain.com",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l1",
      mainnet: null,
      metadata: {},
      name: "PulseChain",
      native_token: "PLS",
      providers: {
        arkham: "pulsechain",
        coingecko_native: "pulsechain",
        moralis: "pulsechain",
        moralis_wallet: "pulsechain"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "pulsechain",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:4663",
      chain_id_hex: "0x1237",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://robinhoodchain.blockscout.com",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l2",
      mainnet: null,
      metadata: {},
      name: "Robinhood Chain",
      native_token: "ETH",
      providers: {
        blockscout_v2: "https://robinhoodchain.blockscout.com/api/v2",
        coingecko: "robinhood",
        coingecko_native: "ethereum",
        defillama: "robinhood",
        evm_rpc: "discovery",
        moralis: "robinhood",
        moralis_wallet: "robinhood"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.mainnet.chain.robinhood.com"
      ],
      slug: "robinhood",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "ron"
      ],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:2020",
      chain_id_hex: "0x7e4",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://app.roninchain.com",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l1",
      mainnet: null,
      metadata: {},
      name: "Ronin",
      native_token: "RON",
      providers: {
        arkham: "ronin",
        coingecko_native: "ronin",
        defillama: "ronin",
        moralis: "ronin",
        moralis_wallet: "ronin"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "ronin",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:2021",
      chain_id_hex: "0x7e5",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://saigon-app.roninchain.com",
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l1",
      mainnet: "eip155:2020",
      metadata: {},
      name: "Ronin Saigon Testnet",
      native_token: "RON",
      providers: {
        moralis: "ronin_saigon"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "ronin_saigon",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "scroll-mainnet"
      ],
      bech32_hrp: null,
      block_time_seconds: 3,
      brand_color: "#EBC28E",
      caip2: "eip155:534352",
      chain_id_hex: "0x82750",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://scrollscan.com",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l2",
      mainnet: null,
      metadata: {},
      name: "Scroll",
      native_token: "ETH",
      providers: {
        codeslaw: "scroll",
        coingecko_native: "ethereum",
        defillama: "scroll",
        dexscreener: "scroll",
        etherscan_scan: "scrollscan.com"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://rpc.scroll.io"
      ],
      slug: "scroll",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 3,
      brand_color: "#EBC28E",
      caip2: "eip155:534351",
      chain_id_hex: "0x8274f",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l2",
      mainnet: "eip155:534352",
      metadata: {},
      name: "Scroll Sepolia",
      native_token: "ETH",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "scroll_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "sei-mainnet"
      ],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:1329",
      chain_id_hex: "0x531",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://seitrace.com",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l1",
      mainnet: null,
      metadata: {},
      name: "Sei",
      native_token: "SEI",
      providers: {
        coingecko_native: "sei-network",
        moralis: "sei",
        moralis_wallet: "sei"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "sei",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:1328",
      chain_id_hex: "0x530",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l1",
      mainnet: "eip155:1329",
      metadata: {},
      name: "Sei Testnet",
      native_token: "SEI",
      providers: {
        moralis: "sei_testnet"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "sei_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 12,
      brand_color: "#627EEA",
      caip2: "eip155:11155111",
      chain_id_hex: "0xaa36a7",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://sepolia.etherscan.io",
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l1",
      mainnet: "eip155:1",
      metadata: {},
      name: "Sepolia",
      native_token: "ETH",
      providers: {
        alchemy: "eth-sepolia",
        ankr: "eth_sepolia",
        infura: "sepolia",
        moralis: "sepolia"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://ethereum-sepolia-rpc.publicnode.com"
      ],
      slug: "sepolia",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "solana-mainnet",
        "0x1399c",
        "sol"
      ],
      bech32_hrp: null,
      block_time_seconds: 0.4,
      brand_color: "#14F195",
      caip2: "solana:5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp",
      chain_id_hex: null,
      decimals: 9,
      explorer_paths: {
        address: "account"
      },
      explorer_url: "https://solscan.io",
      family: "solana",
      finality_blocks: null,
      is_testnet: false,
      layer: null,
      mainnet: null,
      metadata: {},
      name: "Solana",
      native_token: "SOL",
      providers: {
        arkham: "solana",
        blockchair: "solana",
        chainflip: "5",
        coingecko: "solana",
        coingecko_native: "solana",
        defillama: "solana",
        dexscreener: "solana",
        helius: "mainnet",
        thorchain: "SOL"
      },
      reorg_depth: 32,
      rpc_urls: [
        "https://solana-rpc.publicnode.com",
        "https://api.mainnet-beta.solana.com"
      ],
      slug: "solana",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 0.4,
      brand_color: "#14F195",
      caip2: "solana:EtWTRABZaYq6iMfeYKouRu166VU2xqa1",
      chain_id_hex: null,
      decimals: 9,
      explorer_paths: {},
      explorer_url: null,
      family: "solana",
      finality_blocks: null,
      is_testnet: true,
      layer: null,
      mainnet: "solana:5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp",
      metadata: {},
      name: "Solana Devnet",
      native_token: "SOL",
      providers: {
        helius: "devnet"
      },
      reorg_depth: 32,
      rpc_urls: [
        "https://api.devnet.solana.com"
      ],
      slug: "solana-devnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 0.4,
      brand_color: "#14F195",
      caip2: "solana:4uhcVJyU9pJkvQyS88uRDiswHXSCkY3z",
      chain_id_hex: null,
      decimals: 9,
      explorer_paths: {},
      explorer_url: null,
      family: "solana",
      finality_blocks: null,
      is_testnet: true,
      layer: null,
      mainnet: "solana:5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp",
      metadata: {},
      name: "Solana Testnet",
      native_token: "SOL",
      providers: {
        helius: "testnet"
      },
      reorg_depth: 32,
      rpc_urls: [
        "https://api.testnet.solana.com"
      ],
      slug: "solana-testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "thorchain-mainnet"
      ],
      bech32_hrp: "thor",
      block_time_seconds: null,
      brand_color: null,
      caip2: "cosmos:thorchain-1",
      chain_id_hex: null,
      decimals: 8,
      explorer_paths: {
        address: "address",
        block: "block",
        token: null,
        tx: "tx"
      },
      explorer_url: "https://thorchain.net",
      family: "cosmos",
      finality_blocks: null,
      is_testnet: false,
      layer: null,
      mainnet: null,
      metadata: {},
      name: "THORChain",
      native_token: "RUNE",
      providers: {
        coingecko_native: "thorchain",
        thorchain: "THOR"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://gateway.liquify.com/chain/thorchain_api"
      ],
      slug: "thorchain",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "trx",
        "tron-mainnet",
        "tron:0x2b6653dc"
      ],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "tron:728126428",
      chain_id_hex: null,
      decimals: 6,
      explorer_paths: {
        token: "token20",
        tx: "transaction"
      },
      explorer_url: "https://tronscan.org/#",
      family: "tron",
      finality_blocks: null,
      is_testnet: false,
      layer: null,
      mainnet: null,
      metadata: {},
      name: "TRON",
      native_token: "TRX",
      providers: {
        blockchair: "tron",
        chainflip: "7",
        coingecko_native: "tron",
        thorchain: "TRON",
        tronscan: "mainnet"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://api.trongrid.io"
      ],
      slug: "tron",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "tron-testnet-nile",
        "tron:0xcd8690dc"
      ],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "tron:3448148188",
      chain_id_hex: null,
      decimals: 6,
      explorer_paths: {
        token: "token20",
        tx: "transaction"
      },
      explorer_url: "https://nile.tronscan.org/#",
      family: "tron",
      finality_blocks: null,
      is_testnet: true,
      layer: null,
      mainnet: "tron:728126428",
      metadata: {},
      name: "TRON Nile",
      native_token: "TRX",
      providers: {
        tronscan: "nile"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://nile.trongrid.io"
      ],
      slug: "tron-nile",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "tron-testnet-shasta",
        "tron:0x94a9059e"
      ],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "tron:2494104990",
      chain_id_hex: null,
      decimals: 6,
      explorer_paths: {
        token: "token20",
        tx: "transaction"
      },
      explorer_url: "https://shasta.tronscan.org/#",
      family: "tron",
      finality_blocks: null,
      is_testnet: true,
      layer: null,
      mainnet: "tron:728126428",
      metadata: {},
      name: "TRON Shasta",
      native_token: "TRX",
      providers: {
        tronscan: "shasta"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://api.shasta.trongrid.io"
      ],
      slug: "tron-shasta",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "xrpl",
        "ripple",
        "xrp-mainnet",
        "xrpl-mainnet"
      ],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "xrpl:0",
      chain_id_hex: null,
      decimals: 6,
      explorer_paths: {
        address: "account",
        block: "ledger",
        token: null
      },
      explorer_url: "https://xrpscan.com",
      family: "xrpl",
      finality_blocks: null,
      is_testnet: false,
      layer: null,
      mainnet: null,
      metadata: {},
      name: "XRP Ledger",
      native_token: "XRP",
      providers: {
        blockchair: "ripple",
        coingecko_native: "ripple",
        thorchain: "XRP",
        xrpscan: "mainnet"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://s1.ripple.com:51234",
        "https://s2.ripple.com:51234"
      ],
      slug: "xrp",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "xrpl-testnet"
      ],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "xrpl:1",
      chain_id_hex: null,
      decimals: 6,
      explorer_paths: {
        address: "account",
        block: "ledger",
        token: null
      },
      explorer_url: "https://testnet.xrpscan.com",
      family: "xrpl",
      finality_blocks: null,
      is_testnet: true,
      layer: null,
      mainnet: "xrpl:0",
      metadata: {},
      name: "XRP Ledger Testnet",
      native_token: "XRP",
      providers: {
        xrpscan: "testnet"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://s.altnet.rippletest.net:51234"
      ],
      slug: "xrp-testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "zcash-mainnet",
        "zec"
      ],
      bech32_hrp: null,
      block_time_seconds: 75,
      brand_color: "#F4B728",
      caip2: "bip122:00040fe8ec8471911baa1db1266ea15d",
      chain_id_hex: null,
      decimals: 8,
      explorer_paths: {
        token: null,
        tx: "transaction"
      },
      explorer_url: "https://blockchair.com/zcash",
      family: "utxo",
      finality_blocks: null,
      is_testnet: false,
      layer: null,
      mainnet: null,
      metadata: {},
      name: "Zcash",
      native_token: "ZEC",
      providers: {
        blockchair: "zcash",
        blockchair_api: "zcash",
        coingecko_native: "zcash"
      },
      reorg_depth: 24,
      rpc_urls: [],
      slug: "zcash",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:7000",
      chain_id_hex: "0x1b58",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.zetachain.com",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l1",
      mainnet: null,
      metadata: {},
      name: "ZetaChain",
      native_token: "ZETA",
      providers: {
        coingecko_native: "zetachain"
      },
      reorg_depth: 12,
      rpc_urls: [],
      slug: "zetachain",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: null,
      brand_color: null,
      caip2: "eip155:7001",
      chain_id_hex: "0x1b59",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://testnet.zetascan.com",
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l1",
      mainnet: "eip155:7000",
      metadata: {},
      name: "Zetachain Testnet",
      native_token: "ZETA",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "zetachain_testnet",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [
        "zksync",
        "zksync-era",
        "era"
      ],
      bech32_hrp: null,
      block_time_seconds: 1,
      brand_color: "#8C8DFC",
      caip2: "eip155:324",
      chain_id_hex: "0x144",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://explorer.zksync.io",
      family: "evm",
      finality_blocks: null,
      is_testnet: false,
      layer: "l2",
      mainnet: null,
      metadata: {},
      name: "zkSync Era",
      native_token: "ETH",
      providers: {
        alchemy: "zksync-mainnet",
        arkham: "zksync_era",
        coingecko: "zksync",
        coingecko_native: "ethereum",
        defillama: "era",
        dexscreener: "zksync",
        infura: "zksync-mainnet",
        sourcify: "324"
      },
      reorg_depth: 12,
      rpc_urls: [
        "https://mainnet.era.zksync.io"
      ],
      slug: "zksync_era",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 1,
      brand_color: "#8C8DFC",
      caip2: "eip155:300",
      chain_id_hex: "0x12c",
      decimals: 18,
      explorer_paths: {},
      explorer_url: "https://sepolia.explorer.zksync.io",
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l2",
      mainnet: "eip155:324",
      metadata: {},
      name: "zkSync Sepolia",
      native_token: "ETH",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "zksync_sepolia",
      status: "active",
      wrapped_native_address: null
    },
    {
      aliases: [],
      bech32_hrp: null,
      block_time_seconds: 1,
      brand_color: "#8C8DFC",
      caip2: "eip155:280",
      chain_id_hex: "0x118",
      decimals: 18,
      explorer_paths: {},
      explorer_url: null,
      family: "evm",
      finality_blocks: null,
      is_testnet: true,
      layer: "l2",
      mainnet: "eip155:324",
      metadata: {},
      name: "zkSync Goerli",
      native_token: "ETH",
      providers: {},
      reorg_depth: 12,
      rpc_urls: [],
      slug: "zksync_testnet",
      status: "deprecated",
      wrapped_native_address: null
    }
  ],
  provider_keys: {
    alchemy: "Alchemy network subdomain: https://{id}.g.alchemy.com/v2/<key>",
    ankr: "Ankr RPC path: https://rpc.ankr.com/{id}/<key>",
    arkham: "Arkham chain name",
    blockchair: "Blockchair chain name",
    blockchair_api: "Blockchair API chain (UTXO ingest alongside xmrchain)",
    blockscan: "Blockscan chain id",
    blockscout: "Blockscout Etherscan-compatible API base (ABIs, source)",
    blockscout_v2: "Blockscout v2 API base that ingests the chain's transactions",
    blocksec: "BlockSec (MetaSleuth API) chain id",
    chainflip: "Chainflip ForeignChain index, as its vault events encode srcChain / dstChain",
    codeslaw: "Codeslaw platform name",
    coingecko: "CoinGecko asset platform id",
    coingecko_native: "CoinGecko coin id of the chain's native (gas) coin \u2014 what prices its native transfers. Measured per chain, not derived from the platform: CoinGecko's own platform \u2192 native-coin map names AURORA for Aurora and wrapped SEI for Sei, whose gas is ETH and SEI",
    cosmos_directory: "cosmos.directory LCD proxy base URL, the Cosmos fallback",
    cosmos_lcd: "Cosmos SDK LCD (REST) base URL, first choice (Cosmos ingest)",
    defillama: "DefiLlama chain name",
    dexscreener: "DexScreener chain id",
    esplora: "Esplora network (UTXO ingest)",
    etherscan_scan: "Etherscan-family explorer host scraped for labels",
    evm_rpc: "'ingest' (transactions read over the chain's JSON-RPC) or 'discovery' (block windows found over it, transactions read through another route)",
    helius: "Helius cluster (Solana ingest)",
    hyperliquid: "Hyperliquid network (HyperCore ingest)",
    infura: "Infura network subdomain: https://{id}.infura.io/v3/<key>",
    metasleuth: "MetaSleuth chain name",
    moralis: "Moralis chain name; the Moralis ingest route reads every chain carrying it",
    moralis_wallet: "Moralis chain name, where Moralis's mainnet-only WALLET endpoints (wallet chains / history / net worth) accept the chain \u2014 measured per chain, because Moralis's idea of mainnet is not the registry's (it refuses Moonriver)",
    near_rpc: "NEAR archival RPC URL (NEAR ingest)",
    scanscope: "ScanScope chain name",
    sourcify: "Sourcify chain id",
    thorchain: "THORChain chain ticker \u2014 the prefix of its asset notation (ETH in ETH.USDT)",
    tronscan: "TronScan network (TRON ingest)",
    xmrchain: "xmrchain onion-explorer network (Monero ingest)",
    xrpscan: "XRPScan network (XRP Ledger ingest)"
  }
};
var ChainStatus = {
  /** Offered in pickers and processed. */
  ACTIVE: "active"};
var EXPLORER_LINK_TYPES = ["tx", "address", "block", "token"];
var ChainWireSchema = z.object({
  caip2: z.string().min(1),
  slug: z.string().min(1),
  chain_id_hex: z.string().nullable().default(null),
  family: z.string().refine(isChainFamily, { message: "unknown chain family" }),
  aliases: z.array(z.string()).default([]),
  name: z.string().min(1),
  native_token: z.string().min(1),
  decimals: z.number().int().min(0).default(18),
  wrapped_native_address: z.string().nullable().default(null),
  status: z.enum(["active", "deprecated", "disabled"]).default("active"),
  is_testnet: z.boolean().default(false),
  mainnet: z.string().nullable().default(null),
  layer: z.enum(["l1", "l2", "sidechain"]).nullable().default(null),
  // Required: rows arrive resolved (Python fills the mainnet's / family's depth).
  reorg_depth: z.number().int().min(0),
  block_time_seconds: z.number().positive().nullable().default(null),
  finality_blocks: z.number().int().positive().nullable().default(null),
  explorer_url: z.string().nullable().default(null),
  explorer_paths: z.record(z.string(), z.string().nullable()).default({}),
  rpc_urls: z.array(z.string()).default([]),
  providers: z.record(z.string(), z.string()).default({}),
  bech32_hrp: z.string().nullable().default(null),
  brand_color: z.string().nullable().default(null),
  metadata: z.record(z.string(), z.unknown()).default({})
});
function foldAliases(aliases) {
  const seen = /* @__PURE__ */ new Set();
  for (const raw of aliases ?? []) {
    const alias = raw.trim().toLowerCase();
    if (alias) seen.add(alias);
  }
  return [...seen];
}
function explorerPathsOf(raw) {
  const out = {};
  for (const type of EXPLORER_LINK_TYPES) if (type in raw) out[type] = raw[type] ?? null;
  return out;
}
function chainFromWire(row, mainnetSlugOf) {
  const wire = ChainWireSchema.parse(row);
  return Object.freeze({
    caip2: wire.caip2,
    slug: wire.slug,
    chainIdHex: wire.chain_id_hex,
    family: wire.family,
    aliases: Object.freeze(foldAliases(wire.aliases)),
    name: wire.name,
    nativeToken: wire.native_token,
    decimals: wire.decimals,
    wrappedNativeAddress: wire.wrapped_native_address,
    status: wire.status,
    isTestnet: wire.is_testnet,
    mainnet: wire.mainnet,
    mainnetSlug: wire.mainnet ? mainnetSlugOf?.(wire.mainnet) ?? null : null,
    layer: wire.layer,
    reorgDepth: wire.reorg_depth,
    blockTimeSec: wire.block_time_seconds,
    finalityBlocks: wire.finality_blocks,
    explorerUrl: wire.explorer_url ? wire.explorer_url.replace(/\/+$/, "") : null,
    explorerPaths: Object.freeze(explorerPathsOf(wire.explorer_paths)),
    rpcUrls: Object.freeze([...wire.rpc_urls]),
    providers: Object.freeze({ ...wire.providers }),
    bech32Hrp: wire.bech32_hrp,
    brandColor: wire.brand_color,
    logoUrl: null,
    metadata: Object.freeze({ ...wire.metadata })
  });
}
function isActiveChain(chain) {
  return chain.status === ChainStatus.ACTIVE;
}

// src/chains/catalogue.ts
var CatalogueSchema = z.object({
  chains: z.array(ChainWireSchema).default([]),
  provider_keys: z.record(z.string(), z.string()).default({})
});
var SNAPSHOT = CatalogueSchema.parse(catalogue_generated_default);
var BUNDLED_SLUG_BY_CAIP2 = new Map(
  SNAPSHOT.chains.map((row) => [row.caip2, row.slug])
);
var BUNDLED_CHAINS = Object.freeze(
  SNAPSHOT.chains.map((row) => chainFromWire(row, (caip2) => BUNDLED_SLUG_BY_CAIP2.get(caip2)))
);
Object.freeze({
  ...SNAPSHOT.provider_keys
});

// src/chains/registry.ts
var log = createLogger("chains:registry");
function hexKey(value) {
  let token = value.trim().toLowerCase();
  if (!token) return null;
  if (!token.startsWith("0x")) token = `0x${token}`;
  try {
    return `0x${BigInt(token).toString(16)}`;
  } catch {
    return null;
  }
}
var ChainRegistry = class {
  byCaip2 = /* @__PURE__ */ new Map();
  bySlug = /* @__PURE__ */ new Map();
  byHex = /* @__PURE__ */ new Map();
  // Alternate spellings; consulted by `resolve` only after the canonical
  // lookups, so an alias can never shadow a caip2 / hex / slug.
  byAlias = /* @__PURE__ */ new Map();
  listeners = /* @__PURE__ */ new Set();
  revision = 0;
  batchDepth = 0;
  pending = false;
  sorted = null;
  constructor(chains = []) {
    for (const chain of chains) this.register(chain);
    this.pending = false;
  }
  /**
   * Changes whenever the answer to any lookup may have changed — a register,
   * an unregister, a hydration. What `useSyncExternalStore` snapshots.
   */
  get version() {
    return this.revision;
  }
  /**
   * Add `chain`; `replace: true` overwrites the row with the same CAIP-2 id.
   *
   * Throws when the chain's slug or hex id already names a DIFFERENT chain —
   * two rows can never share a spelling, with or without `replace` (Python's
   * `ChainRegistry.register` refuses the same).
   */
  register(chain, { replace = false } = {}) {
    const caip2 = normalizeCaip2(chain.caip2);
    const existing = this.byCaip2.get(caip2);
    if (existing && existing !== chain && !replace) {
      throw new Error(`Chain ${caip2} is already registered; pass replace=true to overwrite.`);
    }
    const hex = chain.chainIdHex ? hexKey(chain.chainIdHex) : null;
    const slugOwner = this.bySlug.get(chain.slug);
    if (slugOwner && normalizeCaip2(slugOwner.caip2) !== caip2) {
      throw new Error(`Chain slug "${chain.slug}" of ${caip2} already names ${slugOwner.caip2}.`);
    }
    const hexOwner = hex ? this.byHex.get(hex) : void 0;
    if (hexOwner && normalizeCaip2(hexOwner.caip2) !== caip2) {
      throw new Error(`Chain hex ${hex} of ${caip2} already names ${hexOwner.caip2}.`);
    }
    if (existing) this.drop(existing);
    this.byCaip2.set(caip2, chain);
    this.bySlug.set(chain.slug, chain);
    if (hex) this.byHex.set(hex, chain);
    for (const alias of foldAliases(chain.aliases)) this.byAlias.set(alias, chain);
    this.touch();
  }
  /** Remove and return the chain `value` resolves to (any spelling). */
  unregister(value) {
    const chain = this.resolve(value);
    if (!chain) return null;
    this.drop(chain);
    this.touch();
    return chain;
  }
  drop(chain) {
    this.byCaip2.delete(normalizeCaip2(chain.caip2));
    if (this.bySlug.get(chain.slug) === chain) this.bySlug.delete(chain.slug);
    const hex = chain.chainIdHex ? hexKey(chain.chainIdHex) : null;
    if (hex && this.byHex.get(hex) === chain) this.byHex.delete(hex);
    for (const alias of foldAliases(chain.aliases)) {
      if (this.byAlias.get(alias) === chain) this.byAlias.delete(alias);
    }
  }
  /** Drop every registered chain (test-only). */
  clear() {
    this.byCaip2.clear();
    this.bySlug.clear();
    this.byHex.clear();
    this.byAlias.clear();
    this.touch();
  }
  /**
   * Run `fn` and notify subscribers ONCE at the end, however many rows it
   * changed — a hydration of a hundred rows is one re-render, not a hundred.
   */
  batch(fn) {
    this.batchDepth += 1;
    try {
      return fn();
    } finally {
      this.batchDepth -= 1;
      if (this.batchDepth === 0 && this.pending) {
        this.pending = false;
        this.emit();
      }
    }
  }
  /** Call `listener` after every change; returns the unsubscribe. */
  subscribe(listener) {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }
  touch() {
    this.revision += 1;
    this.sorted = null;
    if (this.batchDepth > 0) this.pending = true;
    else this.emit();
  }
  emit() {
    for (const listener of [...this.listeners]) {
      try {
        listener();
      } catch (err) {
        log.error("chain registry listener threw", { err });
      }
    }
  }
  /** The chain with this slug (case-folded), or null. */
  get(slug) {
    return this.bySlug.get((slug ?? "").trim().toLowerCase()) ?? null;
  }
  /** Lookup by EVM hex chain id (auto `0x`-prefixes, lowercases, drops leading zeros). */
  getByHex(chainIdHex) {
    const key = hexKey(chainIdHex ?? "");
    return key ? this.byHex.get(key) ?? null : null;
  }
  /** Lookup by CAIP-2 chain id (e.g. "eip155:1"); Base58 references are case-sensitive. */
  getByCaip2(caip2) {
    return this.byCaip2.get(normalizeCaip2(caip2 ?? "")) ?? null;
  }
  /**
   * Resolve by CAIP-2, hex, bare decimal, slug, or alias — in that order, the
   * same order as `ChainRegistry.resolve` in `commons/chains/registry.py`, so a
   * spelling resolves to the same chain on both sides of the wire. Every status
   * resolves.
   */
  resolve(value) {
    if (value == null) return null;
    const token = String(value).trim();
    if (!token) return null;
    if (token.includes(":")) {
      const hit = this.getByCaip2(token);
      if (hit) return hit;
    }
    const folded = token.toLowerCase();
    if (folded.startsWith("0x")) {
      const hit = this.getByHex(folded);
      if (hit) return hit;
    } else if (/^\d+$/.test(folded)) {
      const hit = this.byHex.get(`0x${BigInt(folded).toString(16)}`);
      if (hit) return hit;
    }
    return this.bySlug.get(folded) ?? this.byAlias.get(folded) ?? null;
  }
  /** Every registered chain, whatever its status, sorted by slug. A fresh array. */
  all() {
    this.sorted ??= Object.freeze(
      [...this.byCaip2.values()].sort((a, b) => a.slug.localeCompare(b.slug))
    );
    return [...this.sorted];
  }
  /** The chains offered for new work (status `active`), sorted by slug. */
  active() {
    return this.all().filter(isActiveChain);
  }
  byFamily(family) {
    return this.all().filter((c) => c.family === family);
  }
};
globalSingleton(
  "chains/registry.v2",
  () => new ChainRegistry(BUNDLED_CHAINS)
);
globalSingleton("chains/hydrated-only.v1", () => /* @__PURE__ */ new Set());
function normalizeCaip2(value) {
  const split = value.trim().indexOf(":");
  if (split < 0) return value.trim();
  const namespace = value.trim().slice(0, split).toLowerCase();
  let reference = value.trim().slice(split + 1);
  if (["bip122", "polkadot"].includes(namespace)) reference = reference.toLowerCase();
  if (namespace === "eip155" && /^\d+$/.test(reference)) reference = BigInt(reference).toString();
  return `${namespace}:${reference}`;
}
globalSingleton("chains/explorers", () => ({
  overrides: /* @__PURE__ */ new Map(),
  pathDefaults: {}
}));
var TENANT_PATH_ANY = "*";

// src/tenancy/record-scopes.ts
var TX_SCOPES = [
  {
    id: "user",
    label: "My view",
    description: "Your own private runs, plus everything shared across your workspace.",
    kind: "persisted"
  },
  {
    id: "tenant",
    workspaceLabel: "Workspace",
    workspaceDescription: "Everything shared across your workspace.",
    soloLabel: "Transactions",
    soloDescription: "Every transaction in this workspace.",
    label: "Tenant",
    description: "Everything shared across your current tenant.",
    kind: "persisted"
  },
  {
    id: "org",
    soloLabel: "Transactions",
    soloDescription: "Every transaction in this workspace.",
    label: "Organization",
    description: "The organization-level feed, shared across this org's tenants.",
    kind: "persisted"
  },
  {
    id: "customer",
    soloLabel: "Transactions",
    soloDescription: "Every transaction in this workspace.",
    label: "Customer",
    description: "The customer-level feed, shared across the whole account.",
    kind: "persisted"
  },
  {
    id: "global",
    soloLabel: "All transactions",
    soloDescription: "The shared record of on-chain transactions.",
    label: "Global",
    description: "The shared global record of on-chain transactions, system-wide.",
    kind: "persisted"
  },
  {
    id: "latest",
    label: "Latest blocks",
    description: "Live transactions from the latest block on each preview chain.",
    kind: "live"
  }
];
function txScopeNaming(def, offered) {
  if (def.kind !== "persisted") return { label: def.label, description: def.description };
  const rungs = offered.filter((s) => s.kind === "persisted");
  if (rungs.length <= 1) {
    return {
      label: def.soloLabel ?? def.label,
      description: def.soloDescription ?? def.description
    };
  }
  const wider = rungs.some((s) => s.id === "org" || s.id === "customer" || s.id === "global");
  if (wider) return { label: def.label, description: def.description };
  return {
    label: def.workspaceLabel ?? def.label,
    description: def.workspaceDescription ?? def.description
  };
}
var DEFAULT_TX_SCOPE_ID = "user";
var TX_TENANCY_SCOPES = TX_SCOPES.filter(
  (s) => s.kind === "persisted"
);
var TRANSACTION_SCOPES_KEY = "transaction_scopes";
function scopesFrom(metadata) {
  const raw = metadata?.[TRANSACTION_SCOPES_KEY];
  if (!Array.isArray(raw)) return null;
  const known = raw.filter((v) => typeof v === "string" && TX_SCOPES.some((s) => s.id === v));
  return known.length > 0 ? new Set(known) : null;
}
function offeredTxScopes(planMetadata, subscriptionMetadata = null) {
  const rungs = offeredRungs(planMetadata, subscriptionMetadata);
  const onLadder = offeredOptions(TX_SCOPES, rungs, (s) => s.id);
  const allowed = scopesFrom(subscriptionMetadata) ?? scopesFrom(planMetadata);
  if (!allowed) return onLadder;
  const offered = onLadder.filter((s) => allowed.has(s.id));
  return offered.length > 0 ? offered : onLadder;
}
function offeredTxTenancyScopes(planMetadata, subscriptionMetadata = null) {
  const offered = offeredTxScopes(planMetadata, subscriptionMetadata).filter((s) => s.kind === "persisted");
  return offered.length > 0 ? offered : TX_TENANCY_SCOPES;
}
function offeredTxScopeOr(wanted, offered, ...fallbacks) {
  for (const id of [wanted, ...fallbacks]) if (offered.some((s) => s.id === id)) return id;
  return offered[0]?.id ?? wanted;
}
function isTxTenancyScopeId(value) {
  return TX_TENANCY_SCOPES.some((s) => s.id === value);
}
function txScope(id) {
  return TX_SCOPES.find((s) => s.id === id) ?? TX_SCOPES.find((s) => s.id === DEFAULT_TX_SCOPE_ID);
}
function txScopeOverrides(scope) {
  switch (scope) {
    case "tenant":
      return { user_id: TENANT_PATH_ANY };
    case "org":
      return { tenant_id: GLOBAL_TENANT };
    case "customer":
      return { org_id: GLOBAL_TENANT, tenant_id: GLOBAL_TENANT };
    case "global":
      return { customer_id: GLOBAL_TENANT, org_id: GLOBAL_TENANT, tenant_id: GLOBAL_TENANT };
    case "user":
    case "latest":
    default:
      return {};
  }
}
function txScopeRequest(scope, callerUserId) {
  if (scope === "latest") return { params: {}, degradedTo: null, reason: null };
  if (scope === "user") {
    if (!callerUserId) {
      return {
        params: { scope: "tenant", ...txScopeOverrides("tenant") },
        degradedTo: "tenant",
        reason: "Your own feed needs your user id, and this session has not resolved one \u2014 showing the tenant feed instead."
      };
    }
    return {
      params: { scope: "user", user_id: callerUserId },
      degradedTo: null,
      reason: null
    };
  }
  return { params: { scope, ...txScopeOverrides(scope) }, degradedTo: null, reason: null };
}
function scopeQueryParams(scope, callerUserId) {
  return txScopeRequest(scope, callerUserId).params;
}

// src/tenancy/index.ts
var TenantContext = class _TenantContext {
  static {
    brandClass(this, "tenancy/TenantContext");
  }
  customerId;
  orgId;
  tenantId;
  userId;
  constructor(init = {}) {
    this.customerId = init.customerId ?? null;
    this.orgId = init.orgId || GLOBAL_TENANT;
    this.tenantId = init.tenantId || GLOBAL_TENANT;
    this.userId = init.userId ?? null;
  }
  /** Composite key `{org_id}-{tenant_id}` used in DDB SKs + global-tier checks. */
  get orgIdTenantId() {
    return `${this.orgId}-${this.tenantId}`;
  }
  get isGlobal() {
    return this.orgIdTenantId === GLOBAL_TENANT_COMPOSITE;
  }
  get isUserScoped() {
    return this.userId != null && this.userId.length > 0;
  }
  get hasCustomerScope() {
    return this.customerId != null && this.customerId.length > 0 || this.orgId !== GLOBAL_TENANT;
  }
  withUser(userId) {
    return new _TenantContext({
      customerId: this.customerId,
      orgId: this.orgId,
      tenantId: this.tenantId,
      userId
    });
  }
  withOrg(orgId) {
    return new _TenantContext({
      customerId: this.customerId,
      orgId,
      tenantId: this.tenantId,
      userId: this.userId
    });
  }
  withTenant(tenantId) {
    return new _TenantContext({
      customerId: this.customerId,
      orgId: this.orgId,
      tenantId,
      userId: this.userId
    });
  }
  withCustomer(customerId) {
    return new _TenantContext({
      customerId,
      orgId: this.orgId,
      tenantId: this.tenantId,
      userId: this.userId
    });
  }
  toJSON() {
    return {
      customerId: this.customerId,
      orgId: this.orgId,
      tenantId: this.tenantId,
      userId: this.userId,
      orgIdTenantId: this.orgIdTenantId
    };
  }
};
function isGlobalTenantSegment(value) {
  if (value == null) return true;
  const trimmed = value.trim();
  return trimmed === "" || trimmed === GLOBAL_TENANT;
}
function coerceTenantContext(carrier) {
  if (!carrier) return new TenantContext();
  if (carrier instanceof TenantContext) return carrier;
  return new TenantContext(carrier);
}
function requireExplicitTenant(ctx) {
  if (ctx.isGlobal) {
    throw new Error(
      "Refusing to write under the global tenancy (main-main). Pass an explicit org_id/tenant_id."
    );
  }
}

export { DEFAULT_TX_SCOPE_ID, GLOBAL_TENANT, GLOBAL_TENANT_COMPOSITE, RUNG_ALIASES, RUNG_ORDER, TENANCY_RUNGS_KEY, TRANSACTION_SCOPES_KEY, TX_SCOPES, TX_TENANCY_SCOPES, TenantContext, assertRungSet, canonicalRung, coerceTenantContext, findRootOrg, findRootTenant, isGlobalTenantSegment, isRung, isTxTenancyScopeId, ladderIsChoosable, ladderIsShared, offeredOptions, offeredRungs, offeredTxScopeOr, offeredTxScopes, offeredTxTenancyScopes, requireExplicitTenant, rungOffered, rungOrOffered, scopeQueryParams, selectDefault, selectDefaultOrg, selectDefaultTenant, txScope, txScopeNaming, txScopeOverrides, txScopeRequest };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map